import { extractServerErrorMessage } from '@/utils/errorHandling';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { usePatchAudiencePlayNode } from './usePlaysQuery';

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

interface ISmartNodeUpdateOptions {
  debounceMs?: number;
  onSuccess?: () => void;
  /** Called after a failed save, in addition to the error toast (turn that off with showErrorToast). */
  onError?: (error: any) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  // CRITICAL FIX: Add callback for immediate UI updates
  onOptimisticUpdate?: (updates: Record<string, any>) => void;
}

/**
 * Combine a newly queued node update with the ones still waiting for the debounce. Top-level keys
 * replace; `customActionDetails` merges field by field, so two quick edits to different settings of
 * one step (say the cohort gate's criterion, then another checkbox) both reach the API instead of
 * the second silently dropping the first. Explicit `false`/`null` values are kept.
 */
export const mergePendingNodeUpdates = (
  prev: Record<string, any>,
  updates: Record<string, any>,
): Record<string, any> => {
  const merged = { ...prev, ...updates };
  const before = prev?.customActionDetails;
  const after = updates?.customActionDetails;
  if (before && after && typeof before === 'object' && typeof after === 'object'
    && !Array.isArray(before) && !Array.isArray(after)) {
    merged.customActionDetails = { ...before, ...after };
  }
  return merged;
};

const nodeSaveErrorToastId = (nodeId: number) => `node-save-error-${nodeId}`;

// Saves are numbered across every hook instance (the canvas and the node panel each hold one for
// the same node). Only the newest save to settle for a node changes its error toast, so an older
// save landing late (success or failure) can neither clear nor re-open nor overwrite what a newer
// save already decided.
let lastSaveSeq = 0;
const nextSaveSeq = () => {
  lastSaveSeq += 1;
  return lastSaveSeq;
};
const settledSaveSeqByNode = new Map<number, number>();
/** Record that save `seq` settled for the node; false when a newer save already had. */
const isNewestSettledSave = (nodeId: number, seq: number) => {
  if (seq < (settledSaveSeqByNode.get(nodeId) ?? 0)) return false;
  settledSaveSeqByNode.set(nodeId, seq);
  return true;
};

/**
 * Show why a node save failed, as the one toast for that node. The server's own message comes
 * first: for a step whose settings fail validation it names the setting, the problem and its
 * current value (mb-server playsControllerPatch). The toast stays until dismissed, like the other
 * must-read toasts (utils/linkedinViewBeforeConnect), because every later save of the step fails
 * the same way until the setting is fixed; a repeat failure updates it instead of stacking.
 */
const showNodeSaveErrorToast = (error: any, nodeId: number) => {
  const status = error?.response?.status ?? error?.status;
  let fallback = 'Failed to update node. Please try again.';
  if (status === 404) fallback = 'Node not found. It may have been deleted.';
  else if (status === 409) fallback = 'Node was modified by another user. Please refresh and try again.';
  else if (status === 400) fallback = 'Invalid update data. Please check your inputs.';
  const message = extractServerErrorMessage(error) ?? fallback;

  const toastId = nodeSaveErrorToastId(nodeId);
  if (toast.isActive(toastId)) {
    toast.update(toastId, { render: message });
  } else {
    toast.error(message, { toastId, autoClose: false });
  }
};

export const useSmartNodeUpdates = (
  nodeId: number | undefined,
  workflowId?: number,
  options: ISmartNodeUpdateOptions = {},
) => {
  const {
    debounceMs = 500,
    onSuccess,
    onError,
    showSuccessToast = false,
    showErrorToast = true,
    onOptimisticUpdate,
  } = options;

  const { mutate: patchNode, isPending } = usePatchAudiencePlayNode();
  const [pendingUpdates, setPendingUpdates] = useState<Record<string, any>>({});

  // Store callbacks in refs so the debounced function remains stable across renders.
  // Previously, onSuccess/onError (inline arrow functions from callers) were in the
  // useMemo dependency array, causing the debounce function to be recreated on every
  // render. This broke the debounce timer — each new function had a fresh timeoutId
  // closure, so old timeouts were never cleared, resulting in an API call per keystroke.
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onOptimisticUpdateRef = useRef(onOptimisticUpdate);
  const showSuccessToastRef = useRef(showSuccessToast);
  const showErrorToastRef = useRef(showErrorToast);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onOptimisticUpdateRef.current = onOptimisticUpdate;
  showSuccessToastRef.current = showSuccessToast;
  showErrorToastRef.current = showErrorToast;

  // Runtime guard: only accept real numeric database IDs.
  // Client-side UUIDs (from instanceId/reactFlowNodeId) must never reach the PATCH endpoint.
  const safeNodeId = (typeof nodeId === 'number' && nodeId > 0) ? nodeId : undefined;

  // A save that works clears that node's error toast; a failed one shows it (once — see
  // showNodeSaveErrorToast) and then tells the caller. Both the queued and the immediate path use these.
  const reportSaveSuccess = useCallback((savedNodeId: number, seq: number) => {
    setPendingUpdates({});
    if (isNewestSettledSave(savedNodeId, seq)) toast.dismiss(nodeSaveErrorToastId(savedNodeId));
    onSuccessRef.current?.();
  }, []);
  const reportSaveError = useCallback((error: any, failedNodeId: number, seq: number) => {
    setPendingUpdates({});
    if (isNewestSettledSave(failedNodeId, seq) && showErrorToastRef.current) {
      showNodeSaveErrorToast(error, failedNodeId);
    }
    onErrorRef.current?.(error);
  }, []);

  // Debounced update function — only recreated when nodeId, workflowId, or debounceMs change
  const debouncedUpdate = useMemo(
    () =>
      debounce((updates: Record<string, any>) => {
        if (Object.keys(updates).length > 0 && safeNodeId) {
          const seq = nextSaveSeq();
          patchNode(
            { nodeId: safeNodeId, updates, workflowId },
            {
              onSuccess: () => {
                if (showSuccessToastRef.current) {
                  toast.success('Node updated successfully');
                }
                reportSaveSuccess(safeNodeId, seq);
              },
              onError: (error: any) => reportSaveError(error, safeNodeId, seq),
            },
          );
        }
      }, debounceMs),
    [safeNodeId, workflowId, patchNode, debounceMs, reportSaveSuccess, reportSaveError],
  );

  const queueUpdate = useCallback(
    (updates: Record<string, any>) => {
      if (!safeNodeId) return;

      // CRITICAL FIX: Trigger immediate optimistic update for instant UI feedback
      onOptimisticUpdateRef.current?.(updates);

      setPendingUpdates((prev) => {
        const newUpdates = mergePendingNodeUpdates(prev, updates);
        debouncedUpdate(newUpdates);
        return newUpdates;
      });
    },
    [debouncedUpdate, safeNodeId],
  );

  const queueTitleUpdate = useCallback(
    (title: string) => {
      queueUpdate({ title });
    },
    [queueUpdate],
  );

  const queueDescriptionUpdate = useCallback(
    (description: string) => {
      queueUpdate({ description });
    },
    [queueUpdate],
  );

  const queueCustomActionDetailsUpdate = useCallback(
    (customActionDetails: Record<string, any>) => {
      queueUpdate({ customActionDetails });
    },
    [queueUpdate],
  );

  // Force immediate update (bypasses debouncing)
  const updateImmediately = useCallback(
    (updates: Record<string, any>) => {
      if (!safeNodeId) return;

      const seq = nextSaveSeq();
      patchNode(
        { nodeId: safeNodeId, updates, workflowId },
        {
          onSuccess: () => reportSaveSuccess(safeNodeId, seq),
          onError: (error: any) => reportSaveError(error, safeNodeId, seq),
        },
      );
    },
    [safeNodeId, workflowId, patchNode, reportSaveSuccess, reportSaveError],
  );

  return {
    // Queued updates
    queueUpdate,
    queueTitleUpdate,
    queueDescriptionUpdate,
    queueCustomActionDetailsUpdate,

    // Immediate updates
    updateImmediately,

    // State
    pendingUpdates,
    isPending,

    // Helper to check if there are pending updates
    hasPendingUpdates: Object.keys(pendingUpdates).length > 0,
  };
};
